<!-- fork overlay: fork-owned component (conversation pipeline kanban) — no OSS counterpart. -->
<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useMapGetter } from 'dashboard/composables/store';

import ConversationCard from 'dashboard/components-next/Conversation/ConversationCard/ConversationCard.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  stages: {
    type: Array,
    required: true,
  },
  currentStage: {
    type: String,
    required: true,
  },
  accountLabels: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['move', 'remove']);

const { t } = useI18n();

const inboxById = useMapGetter('inboxes/getInboxById');

const showMenu = ref(false);
const triggerRef = ref(null);
// The column list is an overflow-y-auto scroll container and DropdownMenu is
// plain absolute (no teleport) — near the viewport bottom it would clip, so
// flip it above the button.
const menuOpensUpward = ref(false);
const closeMenu = () => {
  showMenu.value = false;
};
// click-outside runs on the capture phase, BEFORE the trigger's own click —
// ignore trigger clicks here or the toggle would close-then-reopen and the
// menu could never be dismissed from the button (SidebarSortMenu pattern).
const handleClickOutside = event => {
  if (triggerRef.value?.$el?.contains(event.target)) return;
  closeMenu();
};
const toggleMenu = event => {
  if (!showMenu.value) {
    const { bottom } = event.currentTarget.getBoundingClientRect();
    menuOpensUpward.value = window.innerHeight - bottom < 320;
  }
  showMenu.value = !showMenu.value;
};

const stateInbox = computed(
  () => inboxById.value(props.conversation.inboxId) || {}
);
const contact = computed(() => props.conversation.meta?.sender || {});

const menuItems = computed(() => [
  ...props.stages.map(stage => ({
    label: stage,
    value: stage,
    action: 'move',
    icon: 'i-lucide-kanban',
    isSelected: stage === props.currentStage,
    disabled: stage === props.currentStage,
  })),
  {
    label: t('PIPELINE.CARD.REMOVE'),
    action: 'delete',
    icon: 'i-lucide-circle-minus',
  },
]);

const onMenuAction = ({ action, value }) => {
  closeMenu();
  if (action === 'move') emit('move', value);
  if (action === 'delete') emit('remove');
};
</script>

<template>
  <div
    class="relative rounded-xl bg-n-solid-1 outline outline-1 outline-n-container transition-shadow group/pipeline-card focus-within:outline-n-strong hover:shadow-sm"
  >
    <ConversationCard
      :conversation="conversation"
      :contact="contact"
      :state-inbox="stateInbox"
      :account-labels="accountLabels"
      class="rounded-xl"
    />
    <!-- pointer-events-none while hidden: an opacity-0 element still
         hit-tests and would silently swallow card clicks in that corner. -->
    <Button
      ref="triggerRef"
      icon="i-lucide-arrow-left-right"
      size="xs"
      variant="faded"
      color="slate"
      class="absolute top-2 opacity-0 pointer-events-none ltr:right-2 rtl:left-2 group-hover/pipeline-card:opacity-100 group-hover/pipeline-card:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto"
      :aria-label="t('PIPELINE.CARD.MOVE_TO')"
      @click.stop="toggleMenu"
    />
    <DropdownMenu
      v-if="showMenu"
      v-on-click-outside="handleClickOutside"
      :menu-items="menuItems"
      class="z-50 ltr:right-2 rtl:left-2"
      :class="menuOpensUpward ? 'bottom-8' : 'top-10'"
      @action="onMenuAction"
    />
  </div>
</template>
